/*
 * Runtime branding. Components read `brand` — an object with the same shape as
 * the compiled @grapevinetrvl theme packages — so they never care whether the
 * look came from a package or from Airtable.
 *
 * With VUE_APP_BRANDING_SOURCE=airtable the app fetches marvin-core's
 * `clientBranding` query on boot (the Airtable client_branding row for
 * VUE_APP_TMC) and layers it over the static theme. Anything the row leaves
 * blank keeps the static value, and a failed fetch keeps the whole static
 * theme, so a tenant is never rendered blank.
 *
 * Without the flag nothing is fetched: existing tenants (gdg, omega, meon)
 * keep their compiled packages exactly as before.
 */
import _get from 'lodash/get';
import apolloClient from '@/vue-apollo';
import clientBranding from '@/queries/clientBranding';
import { theme } from '@/utils/theme';
import { getContextTMC } from '@/utils/tmc';

export const brandingFromAirtable = () =>
  process.env.VUE_APP_BRANDING_SOURCE === 'airtable';

const SOCIAL_FIELDS = {
  tmcFacebookLink: 'facebook',
  tmcInstaLink: 'instagram',
  tmcLinkedinLink: 'linkedIn',
  tmcTwitterLink: 'twitter'
};

// Exported for the unit test; the getter below is the only runtime caller.
export function mergeBranding(base, remote) {
  if (!remote) {
    return base;
  }
  const social = Object.keys(SOCIAL_FIELDS).reduce((acc, field) => {
    if (remote[field]) {
      acc[SOCIAL_FIELDS[field]] = remote[field];
    }
    return acc;
  }, {});
  return {
    ...base,
    color: remote.brandColor || base.color,
    title: remote.companyName
      ? `YourTrip from ${remote.companyName}`
      : base.title,
    // logo_url is the site logo (unused by the email path, so it can be the
    // on-brand-colour variant); header_logo_url is the email header logo on
    // white and only a fallback here.
    logo: remote.logoUrl || remote.headerLogoUrl || base.logo,
    url: remote.uiUrl || base.url,
    info: {
      ...base.info,
      companyName: remote.companyName || base.info.companyName,
      // the full name (packaged themes keep a short companyName, e.g. "Gray Dawes")
      fullCompanyName: remote.companyName || base.info.fullCompanyName,
      phone: remote.telNumber || base.info.phone
    },
    socialLinks: Object.keys(social).length ? social : base.socialLinks
  };
}

const state = () => ({
  loaded: false,
  source: 'static',
  remote: null
});

const getters = {
  brand: state => mergeBranding(theme, state.remote),
  brandingLoaded: state => state.loaded,
  brandingSource: state => state.source
};

const mutations = {
  setRemote(state, remote) {
    state.remote = remote;
    state.source = remote ? 'airtable' : 'static';
    state.loaded = true;
  },
  setLoaded(state) {
    state.loaded = true;
  }
};

const actions = {
  async fetchBranding({ commit, state }) {
    if (state.loaded) {
      return;
    }
    if (!brandingFromAirtable()) {
      commit('setLoaded');
      return;
    }
    try {
      const response = await clientBranding({
        apollo: apolloClient.defaultClient,
        tmc: getContextTMC()
      });
      commit('setRemote', _get(response, 'data.clientBranding', null));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        'branding: Airtable lookup failed, keeping static theme',
        error
      );
      commit('setLoaded');
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
